import ReactGA from 'react-ga4'
import * as Sentry from '@sentry/react'
import { lazy, Suspense, useEffect } from 'react'
import { Provider } from 'react-redux'
import { BrowserRouter, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import Loader from './components/common/Loader/Loader'
import PrivateRoute from './components/Private-Route/Private-Route'
import ScrollToTop from './components/common/Scroll-To-Top/Scroll-To-Top'
import Store from './lib/Store/Store'
import MetaPixel from './utils/meta/MetaPixel'
import useTokenRefresh from './hooks/useTokenRefresh'
import LabLoader from './components/Lab-Loader/LabLoader'
import { LabWorkspaceProvider } from './contexts/LabWorkspaceContext'
import MessageToast from './components/common/Toasts/MessageToast'
import ErrorBoundary, { RouterErrorBoundary } from './components/common/CustomErrorBoundary'
import { initSentry, SentryRoutes } from './config/sentry'
import { checkAppVersion } from './utils/versionChecker'
import UserLiveClassPageV2 from './components/Course-Content/User-Live-Class/UserLiveClassPageV2'
import UpcomingLiveClassesSectionV2 from './components/Course-Content/Upcoming-Live-Classes/UpcomingLiveClassesSectionV2'
import MainLayout from './layout/MainLayout'
import SubscriptionLayout from './layout/SubscriptionLayout'
import SubscriptionModules from './pages/SubscriptionModules'
import SubscriptionLabs from './pages/SubscriptionLabs'
import SubscriptionModuleLayout from './layout/SubscriptionModuleLayout'
import SubscriptionLabDetailsPage from './pages/SubscriptionLabDetailsPage'
// import { lazyWithRetries } from './utils/retryLazyFunction'

// Home Page
const Login = lazy(() => import('./pages/Login'))
const LandingPage = lazy(() => import('./pages/Landing-Page'))
const Hackathon = lazy(() => import('./pages/Hackathon'))

// Student Profile
const StudentProfile = lazy(() => import('./pages/Student-Profile'))
const EditStudentProfile = lazy(() => import('./pages/Edit-Student-Profile'))

// Payment and Exam
const Payment = lazy(() => import('./pages/Payment'))
const ExamReview = lazy(() => import('./pages/Exam-Review'))
const Exam = lazy(() => import('./pages/Exam'))

// Footer Pages
const AboutUs = lazy(() => import('./pages/About-Us'))
const RefundPolicy = lazy(() => import('./pages/Refund-Policy'))
const TermsAndConditions = lazy(() => import('./pages/Terms-And-Conditions'))
const PrivacyPolicy = lazy(() => import('./pages/Privacy-Policy'))

// Labs
const LabGroupDetails = lazy(() => import('./pages/Lab-Group-Details'))
const AllLabGroups = lazy(() => import('./pages/AllLabGroups'))
const ModuleSectionLayout = lazy(() => import('./layout/ModuleSectionLayout'))
const LabGroupModules = lazy(() => import('./pages/LabGroupModules'))
const SpecificLabDetails = lazy(() => import('./pages/SpecificLabDetails'))
const LabWorkspace = lazy(() => import('./pages/Lab-Workspace'))

// Courses
const AllCourses = lazy(() => import('./pages/All-Courses'))
const MyCourses = lazy(() => import('./pages/My-Courses'))
const WelcomeCoursePage = lazy(() => import('./pages/WelcomeCoursePage'))
const CourseDetails = lazy(() => import('./pages/Specific-Course-Details'))
const CourseContentLayout = lazy(() => import('./layout/CourseContentLayout'))
const UserLiveClassPage = lazy(() =>
  import('./components/Course-Content/User-Live-Class/UserLiveClassPage')
)
const UpcomingLiveClassesSection = lazy(() =>
  import('./components/Course-Content/Upcoming-Live-Classes/UpcomingLiveClassesSection')
)
const PreRecordedClassSection = lazy(() =>
  import('./components/Course-Content/Pre-Recorded-Classes/PreRecordedClassSection')
)

// Subscription
const Subscription = lazy(() => import('./pages/SubscriptionPage'))

const SubscriptionPricing = lazy(() => import('./pages/SubscriptionPricing'))

// Remove the Sentry.init configuration block
initSentry()

const queryClient = new QueryClient()

const App = () => {
  ReactGA.initialize('G-LE7V2ZWEWZ')
  useTokenRefresh()

  useEffect(() => {
    checkAppVersion()
  }, [])

  return (
    <Provider store={Store}>
      <MetaPixel />
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename="/">
          <ScrollToTop>
            <ErrorBoundary>
              <Suspense fallback={<Loader />}>
                <MessageToast position="top-right" autoClose={5000} theme="dark" />
                <SentryRoutes>
                  <Route path="/" element={<MainLayout />} errorElement={<RouterErrorBoundary />}>
                    <Route index element={<LandingPage />} />
                    <Route path="/login" element={<Login />} />
                    {/* Hackathon */}
                    <Route path="/hackathon" element={<Hackathon />} />
                    <Route path="/*" element={<PrivateRoute />}>
                      <Route path="student">
                        <Route path="profile" element={<StudentProfile />} />
                        <Route path="edit-profile" element={<EditStudentProfile />} />
                      </Route>
                      {/* Payment and Exam Routes */}
                      <Route path="payment/:paymentID/*" element={<Payment />} />
                      <Route path="exam/:courseID/:chapterID/:examID" element={<Exam />} />
                      <Route
                        path="review-exam/:courseID/:chapterID/:examID/:userID"
                        element={<ExamReview />}
                      />
                      {/* Lab Routes */}
                      <Route path="all-lab-groups" element={<AllLabGroups />} />
                      <Route path="lab-group-modules/:labGroupID" element={<ModuleSectionLayout />}>
                        <Route index element={<LabGroupModules />}></Route>
                        <Route path=":moduleID" element={<LabGroupDetails />} />
                      </Route>
                      <Route
                        path="lab-details/lab-group-modules/:labGroupID"
                        element={<ModuleSectionLayout />}>
                        <Route index element={<LabGroupModules />} />
                        <Route path=":moduleID" element={<LabGroupDetails />} />
                      </Route>
                      <Route
                        path="lab-details/lab-group-modules/:labGroupID/:moduleID/:labID"
                        element={<SpecificLabDetails />}
                      />
                      <Route
                        path="lab-group-modules/:labGroupID/:moduleID/:labID"
                        element={<SpecificLabDetails />}
                      />

                      <Route path=":plan/lab-loading" element={<LabLoader />} />
                      <Route
                        path="launch/playground/:plan/:labGroupID/:moduleID/:labID"
                        element={
                          <LabWorkspaceProvider>
                            <LabWorkspace />
                          </LabWorkspaceProvider>
                        }
                      />

                      {/* My Courses Routes */}
                      <Route path="my-courses" element={<MyCourses />} />
                      {/* Old Version */}
                      <Route path="course-content/v1/:courseID" element={<CourseContentLayout />}>
                        <Route index element={<WelcomeCoursePage />} />
                        {/* For live class */}
                        <Route
                          path="live-class/:moduleIndex/:subModuleIndex"
                          element={<UpcomingLiveClassesSection />}
                        />
                        <Route
                          path=":moduleIndex/:subModuleIndex/:classID"
                          element={<UserLiveClassPage />}
                        />
                        <Route
                          path="pre-recorded/:moduleIndex/:subModuleIndex"
                          element={<PreRecordedClassSection />}
                        />
                        <Route
                          path="pre-recorded/:moduleIndex/:subModuleIndex/:folderIndex/:lectureIndex"
                          element={<UserLiveClassPage />}
                        />
                      </Route>
                      {/* New Version */}
                      <Route path="course-content/v2/:courseID" element={<CourseContentLayout />}>
                        <Route index element={<WelcomeCoursePage />} />
                        {/* For live class */}
                        <Route
                          path="live-class/:moduleIndex/:contentIndex"
                          element={<UpcomingLiveClassesSectionV2 />}
                        />
                        <Route
                          path=":moduleIndex/:contentIndex"
                          element={<UserLiveClassPageV2 />}
                        />
                      </Route>
                    </Route>
                    {/* Course Routes */}
                    <Route path="/all-courses" element={<AllCourses />} />

                    {/* Course Overview */}
                    <Route path="/course-details/:courseID" element={<CourseDetails />} />

                    {/* Subscription */}
                    <Route path="/subscription" element={<SubscriptionLayout />}>
                      <Route index element={<Subscription />} />
                      <Route path="pricing" element={<SubscriptionPricing />} />
                      <Route path=":plan/:labGroupId" element={<SubscriptionModuleLayout />}>
                        <Route index element={<SubscriptionModules />} />
                        <Route path=":moduleId" element={<SubscriptionLabs />} />
                      </Route>
                      <Route
                        path=":plan/:labGroupId/:moduleId/:labId"
                        element={<SubscriptionLabDetailsPage />}
                      />
                    </Route>

                    {/* Footer Routes */}
                    <Route path="/about-us" element={<AboutUs />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/refund-policy" element={<RefundPolicy />} />
                    <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                  </Route>
                </SentryRoutes>
              </Suspense>
            </ErrorBoundary>
          </ScrollToTop>
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>
  )
}

export default Sentry.withProfiler(App)
